// Leaf import, not the "@/modules/_" barrel: the barrel re-exports the
// viewport hooks, which reach Lenis and the gsap plugin set. This module only
// needs mount and destroy.
import { onDestroy, onMount } from "@/modules/_/runner";
// Core gsap rather than @lib/gsap, so the Sculpt bundle doesn't carry
// ScrollTrigger, Draggable, Inertia, Flip, CustomEase and CustomWiggle for a
// module that only tweens opacity, colour and scale. Every tween here passes
// its own duration and ease, so @lib/gsap's defaults are nothing to inherit.
import gsap from "gsap";
import { prefersReducedMotion } from "@utils/media";

const reduced = prefersReducedMotion();
import { Resize } from "@lib/subs";
import {
  SCULPT_SHAPES,
  sculptShapeMask,
  preloadSculptShapes,
} from "@/utils/sculpt-shapes";

// Card anatomy, as Webflow builds it:
//
//   card-item  .card-theme.is--alt    `is--active` flips `color` to text-alternate
//     card       .last_card           z-index 1, sits over the bg and the blobs
//       avatar     + info-wrap        the resting face: portrait, name, role, logo
//       info-desc  .speaker_desc      absolute inset:0 — the bio, stacked on top
//     blob-wrap  > blob-lg, blob-sm   absolute, behind the card
//     card-bg    .last_card_bg        `is--active` paints the dark panel
//
// Layout and the colour swap are CSS — `speakers_column` handles the columns at
// each breakpoint, and the panel transitions its own colour, which is per-speaker
// and reaches the page as an inline `color` on card-bg from the CMS. JS owns the
// two things CSS here can't. First the crossfade: the bio is absolutely
// positioned over the face, so at full opacity they render on top of each other
// and one has to fade out as the other fades in. Second the blob pop, whose
// overshoot and stagger don't survive being expressed as a transition.
// The resting panel is a global token, read at mount rather than hardcoded so
// the palette stays owned by the Designer. The other two panel colours are
// per-speaker and come from the CMS — see openBgs / hoverBgs below.
const REST_BG_TOKEN = "--_default---oat--oat-200";
const REST_BG_FALLBACK = "#f4f3f0";
const HOVER_BG_FALLBACK = "#fbe189";

// The bio only ever shows on the dark panel, so its text is pinned light once at
// mount. Left to inherit, it picks up the card's `color` — which Webflow
// transitions over .35s — and visibly animates from dark to white as it fades
// in, which reads as the text flashing before the bio has arrived.
const ALT_TEXT_TOKEN = "--_default---text-color--text-alternate";
const ALT_TEXT_FALLBACK = "#fefdfb";

// One motion family. Everything except the blobs runs on the same ease, because
// a click fires four things at once — the panel recolours, the portrait swaps,
// the face leaves and the bio arrives — and four different curves finishing at
// four different moments is what makes it read as separate animations rather
// than one gesture.
const EASE = "power2.out";

// Deliberately the quickest thing here, and quicker than the face takes to
// leave. If the panel is still mid-tone while the face fades, the face's dark
// text sits at partial opacity over a half-dark background and washes out to
// something that reads as white. Getting the panel dark first means the face
// fades out against a colour it already disappears into.
//
// The panel tint and the portrait regaining colour are the same beat, so they
// share a duration.
const PANEL_DURATION = 0.22;

// The face leaves before the bio arrives rather than crossfading through it —
// two legible layers at half opacity is the overlap this exists to avoid. The
// small delays are what turn "everything at once" into a sequence you can read.
//
// The bio's delay is also doing contrast work: it waits until the panel is most
// of the way to dark, so light text never lands on a panel that's still pale.
const FACE_OUT_DURATION = 0.15;
const FACE_IN_DURATION = 0.32;
const FACE_IN_DELAY = 0.06;
const DESC_IN_DURATION = 0.32;
const DESC_IN_DELAY = 0.16;
const DESC_OUT_DURATION = 0.18;

// The blobs keep their own curve and their own length — the overshoot is the
// point, and it's the one piece of this that already read correctly. It starts
// with everything else and is deliberately the last thing to settle, so the pop
// reads as the accent on the end of the gesture rather than a separate one.
const BLOB_SCALE = 0.8;
const BLOB_IN_DURATION = 0.8;
const BLOB_IN_EASE = "back.out(1.2)";
const BLOB_IN_DELAY = 0.05;
const BLOB_OUT_DURATION = 0.3;
const BLOB_STAGGER = 0.06;

// Webflow's mobile-landscape breakpoint, where `speakers_column` becomes a flex
// stack and the card face turns horizontal — which leaves the card about 139px
// tall against a bio that needs 210. Rather than grow the card or crop the text,
// the reveal is simply a tablet-and-up affordance: below this the cards are
// static. Keep in step with the 767px media query if that layout ever moves.
const INTERACTIVE_MIN_WIDTH = 768;

// Breathing room under the last line of a bio on a card we had to grow. The
// 1.25rem the design puts between the name and the text reads too tight down
// here, against a 2.5rem corner radius, so this sits a step above it — close to
// the slack the cards that already fit happen to carry.
const BIO_BOTTOM_GAP = 32;

export default function (element: HTMLElement, dataset: DOMStringMap = {}) {
  const list = element.querySelector<HTMLElement>('[speaker-ticker="cms-list"]');
  const items = list
    ? Array.from(
        list.querySelectorAll<HTMLElement>('[speaker-ticker="cms-item"]'),
      )
    : [];

  if (!list || items.length === 0) return;

  const part = (item: HTMLElement, name: string) =>
    item.querySelector<HTMLElement>(`[speaker-ticker="${name}"]`);

  const cards = items.map((item) => part(item, "card") ?? item);
  const avatars = items.map((item) => part(item, "avatar"));
  const cardBgs = items.map((item) => part(item, "card-bg"));
  const cardItems = items.map((item) => part(item, "card-item"));
  const descs = items.map((item) => part(item, "info-desc"));
  // The resting face is "everything in the card except the bio", derived rather
  // than listed. The parts move around in the Designer — the logo was inside
  // info-wrap until it needed bottom-justifying, at which point a hardcoded
  // [avatar, info-wrap] list quietly stopped covering it and the logo sat on top
  // of the open card. Taking the card's children minus info-desc survives that.
  const faces = items.map((item, i) => {
    const desc = descs[i];
    return Array.from(cards[i].children).filter(
      (node): node is HTMLElement => node instanceof HTMLElement && node !== desc,
    );
  });
  const blobs = items.map((item) =>
    Array.from(
      item.querySelectorAll<HTMLElement>(
        '[speaker-ticker="blob-lg"], [speaker-ticker="blob-sm"]',
      ),
    ),
  );
  const allBlobs = blobs.flat();
  const allFades = [...faces.flat(), ...descs.filter(Boolean)] as HTMLElement[];

  // Two independent pieces of state, because the card has three looks, not two:
  // resting (cream panel, greyscale portrait), hovered (lemon panel, portrait in
  // colour, same face) and open (the speaker's own dark panel, blobs, bio). Hover
  // is a pointer/focus hint; open is a deliberate click. Keeping them apart is
  // what stops the pointer from yanking a bio open or shut on the way past.
  let hoverIndex = -1;
  let openIndex = -1;
  let interactive = window.innerWidth >= INTERACTIVE_MIN_WIDTH;

  // Every speaker ships their own palette from the CMS, as inline `color` on
  // three elements: the dark panel on card-bg, plus one colour per blob. Read
  // them once — from here on JS owns background-color on card-bg, so the
  // class-driven `currentColor` rule no longer gets a say.
  //
  // Hover borrows the large blob's colour, so the tint previews part of the same
  // palette the open card then pays off in full — the pale yellow in the design
  // file is one speaker's blob, not a global hover colour. Large rather than
  // small because the card keeps its dark text while hovered: across the current
  // 14 speakers every Blob Lg clears 4.5:1 against it (worst 6.3), while three
  // Blob Sm values land near 3.2 and would be unreadable. If a speaker ever
  // needs a hover colour of their own, add the CMS field and read it here first,
  // keeping this as the fallback.
  const inlineColor = (node: HTMLElement | null) =>
    node ? getComputedStyle(node).color : "";
  const openBgs = cardBgs.map((bg) => inlineColor(bg) || REST_BG_FALLBACK);
  const hoverBgs = items.map(
    (item) =>
      inlineColor(part(item, "blob-lg")) ||
      inlineColor(part(item, "blob-sm")) ||
      HOVER_BG_FALLBACK,
  );
  const restBg =
    getComputedStyle(element).getPropertyValue(REST_BG_TOKEN).trim() ||
    REST_BG_FALLBACK;

  function tween(
    target: HTMLElement | HTMLElement[],
    vars: gsap.TweenVars,
    snap: boolean,
  ) {
    if (!snap) {
      gsap.to(target, { ...vars, overwrite: "auto" });
      return;
    }
    // Drop the timing keys rather than handing them to gsap.set, which would
    // still honour delay and stagger and leave the "instant" state arriving late.
    const { duration, ease, stagger, delay, ...props } = vars;
    gsap.set(target, props);
  }

  function paint(snap = reduced) {
    items.forEach((item, i) => {
      const open = i === openIndex;
      // A card can be pointed at while another is open; it just doesn't get the
      // hover look, because open already outranks it.
      const hot = !open && i === hoverIndex;

      if (open) cards[i].setAttribute("data-active", "true");
      else cards[i].removeAttribute("data-active");
      // `is--active` is what flips the text to the light colour, so it stays
      // tied to the dark open panel. The hover tint is light and keeps dark text.
      cardBgs[i]?.classList.toggle("is--active", open);
      cardItems[i]?.classList.toggle("is--active", open);
      // Hook for moving the hover look into the Designer later, at which point
      // the tint below can come out; nothing styles it today.
      cardBgs[i]?.classList.toggle("is--hover", hot);
      cardItems[i]?.classList.toggle("is--hover", hot);
      item.setAttribute("aria-expanded", open ? "true" : "false");

      const bg = cardBgs[i];
      if (bg)
        tween(
          bg,
          {
            backgroundColor: open ? openBgs[i] : hot ? hoverBgs[i] : restBg,
            duration: PANEL_DURATION,
            ease: EASE,
          },
          snap,
        );

      const avatar = avatars[i];
      if (avatar)
        tween(
          avatar,
          {
            // Colour is the reward for pointing at a card, so it arrives with
            // the tint rather than waiting for the click.
            filter: open || hot ? "grayscale(0%)" : "grayscale(100%)",
            duration: PANEL_DURATION,
            ease: EASE,
          },
          snap,
        );

      const blobs_ = blobs[i];
      if (blobs_.length) {
        if (snap) gsap.killTweensOf(blobs_);
        tween(
          blobs_,
          {
            opacity: open ? 1 : 0,
            scale: open ? 1 : BLOB_SCALE,
            duration: open ? BLOB_IN_DURATION : BLOB_OUT_DURATION,
            ease: open ? BLOB_IN_EASE : EASE,
            delay: open ? BLOB_IN_DELAY : 0,
            stagger: BLOB_STAGGER,
          },
          snap,
        );
      }

      // autoAlpha, not opacity: the hidden layer has to stop taking pointer
      // events and drop out of the accessibility tree, but keep its box so the
      // card can't change height as the two sides swap.
      const face = faces[i];
      if (face.length)
        tween(
          face,
          {
            autoAlpha: open ? 0 : 1,
            duration: open ? FACE_OUT_DURATION : FACE_IN_DURATION,
            ease: EASE,
            delay: open ? 0 : FACE_IN_DELAY,
          },
          snap,
        );

      const desc = descs[i];
      if (desc)
        tween(
          desc,
          {
            autoAlpha: open ? 1 : 0,
            duration: open ? DESC_IN_DURATION : DESC_OUT_DURATION,
            ease: EASE,
            delay: open ? DESC_IN_DELAY : 0,
          },
          snap,
        );
    });
  }

  function setHover(index: number) {
    if (index === hoverIndex) return;
    hoverIndex = index;
    paint();
  }

  function setOpen(index: number) {
    if (index === openIndex) return;
    openIndex = index;
    paint();
  }

  // The bio is absolutely positioned, so it adds nothing to the card's height —
  // that comes entirely from the resting face — while `.last_card` clips its
  // overflow. In the four-column range the columns are narrow enough that the
  // text outgrows the face, and half the cards lose their last line or two
  // somewhere between roughly 992 and 1500px. So measure what each bio actually
  // needs and reserve it: the row stretches to its tallest card, nothing crops,
  // and where the text already fits this writes nothing at all — which is also
  // what keeps it safe if the CSS later shrinks the type.
  //
  // Reserve on the card, not on the grid item. `.last_card` is the box carrying
  // `overflow: clip`, and above 991px it has no `min-height: 100%`, so it sizes
  // to its own content and simply sits centred in a taller row. Growing the row
  // leaves the clipping box exactly as small as it was.
  //
  // This also depends on the desc's children sizing to their content. If they
  // ever go back to `height: 100%` they'll soak up the reserved space and slide
  // the text down with it, and no amount of reserving will catch up.
  function reserveBioHeight() {
    cards.forEach((card) => card.style.removeProperty("min-height"));
    // Below the breakpoint the bio never opens, so there's nothing to fit.
    if (!interactive) return;

    // Measure every card before writing any of them back, to keep this to one
    // layout pass rather than one per card.
    const needed = cards.map((card, i) => {
      const desc = descs[i];
      if (!desc) return 0;
      // Leaves only: a wrapper stretched by its parent reports the card's own
      // bottom, which would read as "fits" no matter how far the text overruns.
      const leaves = Array.from(desc.querySelectorAll<HTMLElement>("*")).filter(
        (node) => !node.children.length && node.textContent?.trim(),
      );
      if (!leaves.length) return 0;
      const top = card.getBoundingClientRect().top;
      const bottom = leaves.reduce(
        (low, node) => Math.max(low, node.getBoundingClientRect().bottom),
        0,
      );
      return Math.ceil(bottom - top + BIO_BOTTOM_GAP);
    });

    cards.forEach((card, i) => {
      if (needed[i] > card.getBoundingClientRect().height)
        card.style.minHeight = `${needed[i]}px`;
    });
  }

  function syncInteractivity() {
    items.forEach((item) => {
      if (interactive) {
        item.setAttribute("role", "button");
        item.setAttribute("tabindex", "0");
        item.setAttribute("aria-expanded", "false");
        item.style.cursor = "pointer";
      } else {
        // Only drop the role if it's ours — the markup may carry its own.
        if (item.getAttribute("role") === "button") item.removeAttribute("role");
        item.removeAttribute("tabindex");
        item.removeAttribute("aria-expanded");
        item.style.removeProperty("cursor");
      }
    });
  }

  let alive = true;

  onMount(() => {
    if (dataset.shapes !== "false") {
      preloadSculptShapes();
      // Deterministic per index, not shuffled: the grid is static, so a deck
      // that reshuffles every page load reads as instability rather than variety.
      avatars.forEach((avatar, i) => {
        if (!avatar) return;
        const mask = sculptShapeMask(i % SCULPT_SHAPES.length);
        gsap.set(avatar, {
          webkitMaskImage: mask,
          maskImage: mask,
          webkitMaskSize: "100% 100%",
          maskSize: "100% 100%",
          webkitMaskRepeat: "no-repeat",
          maskRepeat: "no-repeat",
        });
      });
    }

    gsap.set(items, { userSelect: "none", touchAction: "manipulation" });
    const altText =
      getComputedStyle(element).getPropertyValue(ALT_TEXT_TOKEN).trim() ||
      ALT_TEXT_FALLBACK;
    descs.forEach((desc) => desc && gsap.set(desc, { color: altText }));
    paint(true);

    syncInteractivity();
    reserveBioHeight();
    // Webfonts land after mount and change the line count, so measure again
    // once they're in.
    document.fonts?.ready.then(() => alive && reserveBioHeight());

    const cleanups: Array<() => void> = [];

    const hoverCapable = window.matchMedia(
      "(hover: hover) and (pointer: fine)",
    ).matches;

    items.forEach((item, i) => {
      // Hover only tints; it never opens or closes a bio. Sweeping across the
      // grid is therefore free — the open card stays put until you act on it.
      const onEnter = () => interactive && setHover(i);
      const toggle = () => interactive && setOpen(openIndex === i ? -1 : i);
      const onClick = () => toggle();
      const onKey = (event: KeyboardEvent) => {
        if (event.key === "Escape") {
          setOpen(-1);
          return;
        }
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        toggle();
      };
      // Keyboard gets the same tint as the pointer, so tabbing through reads the
      // same as hovering; Enter is still what opens the bio.
      const onFocus = () => interactive && setHover(i);
      const onBlur = () => interactive && hoverIndex === i && setHover(-1);

      if (hoverCapable) item.addEventListener("pointerenter", onEnter);
      item.addEventListener("click", onClick);
      item.addEventListener("keydown", onKey);
      item.addEventListener("focus", onFocus);
      item.addEventListener("blur", onBlur);

      cleanups.push(() => {
        item.removeEventListener("pointerenter", onEnter);
        item.removeEventListener("click", onClick);
        item.removeEventListener("keydown", onKey);
        item.removeEventListener("focus", onFocus);
        item.removeEventListener("blur", onBlur);
        // Same care as syncInteractivity: the markup ships its own listitem role.
        if (item.getAttribute("role") === "button") item.removeAttribute("role");
        ["tabindex", "aria-expanded"].forEach((attr) =>
          item.removeAttribute(attr),
        );
        item.style.removeProperty("cursor");
      });
    });

    if (hoverCapable) {
      // Drops the tint on the way out; an open bio is left alone, since opening
      // it was deliberate.
      const onLeave = () => setHover(-1);
      element.addEventListener("pointerleave", onLeave);
      cleanups.push(() => element.removeEventListener("pointerleave", onLeave));
    }

    // Clicking away closes the open card. Every device now, not just touch:
    // opening is a click on both, so closing should be too.
    const onOutside = (event: PointerEvent) => {
      if (openIndex === -1) return;
      if (element.contains(event.target as Node)) return;
      setOpen(-1);
    };
    document.addEventListener("pointerdown", onOutside);
    cleanups.push(() => document.removeEventListener("pointerdown", onOutside));

    // The stack and the grid swap at the same 767px line, so the affordance has
    // to flip with them rather than being decided once at mount. Bios rewrap at
    // every width, so the reservation is recomputed on every resize, not just
    // when the breakpoint changes.
    const offResize = Resize.add((data: { width: number }) => {
      if (!alive) return;
      const next = data.width >= INTERACTIVE_MIN_WIDTH;
      if (next !== interactive) {
        interactive = next;
        if (!interactive) {
          hoverIndex = -1;
          openIndex = -1;
          paint(true);
        }
        syncInteractivity();
      }
      reserveBioHeight();
    });

    onDestroy(() => {
      alive = false;
      offResize();
      cards.forEach((card) => card.style.removeProperty("min-height"));
      cleanups.forEach((fn) => fn());
      // card-bg included: JS took over its background-color, so handing the
      // element back means giving that rule to CSS again.
      const owned = [
        ...allBlobs,
        ...allFades,
        ...(cardBgs.filter(Boolean) as HTMLElement[]),
        ...(avatars.filter(Boolean) as HTMLElement[]),
      ];
      gsap.killTweensOf(owned);
      gsap.set(items, { clearProps: "all" });
      gsap.set(owned, { clearProps: "all" });
      cards.forEach((card) => card.removeAttribute("data-active"));
      ["is--active", "is--hover"].forEach((cls) => {
        cardBgs.forEach((bg) => bg?.classList.remove(cls));
        cardItems.forEach((cardItem) => cardItem?.classList.remove(cls));
      });
    });
  });
}
